/**
 * Cross Plot Scatter Chart Component
 *
 * Renders a scatter plot for well stimulation data with median lines,
 * type curve reference lines, and color-by-attribute support.
 * Hover / highlight pattern mirrors probit-chart.tsx.
 */

import { type FC, type ReactNode, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';
import {
  SciChartSurface,
  SciChartJSLightTheme,
  NumericAxis,
  XyScatterRenderableSeries,
  XyDataSeries,
  EllipsePointMarker,
  SquarePointMarker,
  TrianglePointMarker,
  XPointMarker,
  BasePointMarker,
  IPointMarkerOptions,
  TSciChart,
  VerticalLineAnnotation,
  HorizontalLineAnnotation,
  CustomAnnotation,
  MouseWheelZoomModifier,
  ZoomPanModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  CursorModifier,
  ChartModifierBase2D,
  ModifierMouseArgs,
  IPointMetadata,
  EAutoRange,
  EAxisAlignment,
  NumberRange,
  EAnnotationLayer,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  NumericLabelProvider,
  Thickness,
} from 'scichart';
import { formatNumber } from 'utils/number';
import { parseMomentUtc } from 'utils/dates';
import type {
  CrossPlotAxisValueType,
  CrossPlotData,
} from 'types/type-curve-cross-plot';
import {
  SciChartExportImageButton,
  createSciChartElementHooks,
} from 'components/chart-export';
import { ensureWasmConfigured } from 'components/charts/scichart/ensure-wasm-configured';
import styles from './cross-plot.module.scss';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DEFAULT_POINT_COLOR = '#4589ff';
const AXIS_LABEL_STYLE = { fontSize: 11, padding: new Thickness(0, 6, 0, 0) };
const AXIS_TITLE_STYLE = { fontSize: 12 };
const HIT_TEST_RADIUS = 12;
const POINT_MARKER_SIZE = 10;
const POINT_MARKER_STROKE = 1.75;
// Thin black outline drawn around every filled marker so points stay
// distinguishable from background / median lines regardless of their fill.
const POINT_MARKER_BORDER_COLOR = '#000000';
const POINT_MARKER_BORDER_THICKNESS = 1;

// ---------------------------------------------------------------------------
// Status → marker mapping
//
// Each well status renders with its own marker shape so the user can tell
// PRODUCING / DUC / PERMIT / SHUT_IN / EXPIRED_PERMIT / INACTIVE wells apart
// at a glance. "Without fill" variants (EXPIRED_PERMIT, INACTIVE) use a
// transparent fill + colored stroke so only the shape's outline shows.
//
// Canonical status keys come from `app/lib/well_color_picker.rb` and the
// cross-plot Elasticsearch response (uppercased, underscore-separated).
// ---------------------------------------------------------------------------
type PointMarkerCtor = new (
  wasmContext: TSciChart,
  options?: IPointMarkerOptions
) => BasePointMarker;

interface StatusMarkerCfg {
  Marker: PointMarkerCtor;
  filled: boolean;
}

const STATUS_MARKER: Record<string, StatusMarkerCfg> = {
  PRODUCING: { Marker: EllipsePointMarker, filled: true },
  DUC: { Marker: SquarePointMarker, filled: true },
  PERMIT: { Marker: TrianglePointMarker, filled: true },
  SHUT_IN: { Marker: XPointMarker, filled: true },
  EXPIRED_PERMIT: { Marker: TrianglePointMarker, filled: false },
  INACTIVE: { Marker: XPointMarker, filled: false },
};

const DEFAULT_STATUS_MARKER: StatusMarkerCfg = {
  Marker: EllipsePointMarker,
  filled: true,
};

// Normalize an API status to the canonical key used in STATUS_MARKER:
// upper-cases, trims, and turns spaces / hyphens into underscores so
// "Shut In", "shut-in", "SHUT_IN" all match the same bucket.
function normalizeStatus(status: string | null | undefined): string {
  return (status ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
}

function getStatusMarker(status: string | null | undefined): StatusMarkerCfg {
  return STATUS_MARKER[normalizeStatus(status)] ?? DEFAULT_STATUS_MARKER;
}

// Build a point marker for a given status + color. Filled statuses
// (PRODUCING, DUC, PERMIT, SHUT_IN) get a thin black border so the shape
// stays readable against any fill color. Unfilled statuses (EXPIRED_PERMIT,
// INACTIVE) use a transparent fill + visible colored stroke so only the
// colored outline of the marker is drawn — keeping their colored stroke
// preserves the color-by-attribute encoding.
function buildPointMarker(
  wasmContext: TSciChart,
  status: string | null | undefined,
  color: string
): BasePointMarker {
  const cfg = getStatusMarker(status);
  return new cfg.Marker(wasmContext, {
    width: POINT_MARKER_SIZE,
    height: POINT_MARKER_SIZE,
    fill: cfg.filled ? color : 'transparent',
    stroke: cfg.filled ? POINT_MARKER_BORDER_COLOR : color,
    strokeThickness: cfg.filled
      ? POINT_MARKER_BORDER_THICKNESS
      : POINT_MARKER_STROKE,
  });
}

// Extract the unit from an axis label like "Proppant Intensity [lbs/ft]"
// or "Fluid Intensity (bbl/ft)" — backend returns either bracket form.
function extractUnit(axisLabel: string): string {
  const bracketMatch = axisLabel.match(/\[([^\]]+)\]/);
  if (bracketMatch) return bracketMatch[1];
  const parenMatch = axisLabel.match(/\(([^)]+)\)/);
  if (parenMatch) return parenMatch[1];
  return '';
}

// Extract a short name from an axis label, e.g. "Proppant Intensity [lbs/ft]" → "Proppant".
function extractShortName(axisLabel: string): string {
  const withoutUnit = axisLabel.replace(/\s*[[(][^\])]+[\])]/, '').trim();
  return withoutUnit.split(' ')[0] || axisLabel;
}

// ---------------------------------------------------------------------------
// Axis value formatting
//
// A date is read as UTC, the same basis the server encodes it in, so that a
// date-only value such as "2013-11-25" never renders a day early.
// ---------------------------------------------------------------------------
const isDateAxis = (valueType: CrossPlotAxisValueType | undefined): boolean =>
  valueType === 'date';

// Ticks stay compact; tooltips and reference-line captions show the full date.
const formatDateTick = (epochMs: number): string =>
  parseMomentUtc(epochMs).format('MMM YYYY');

function formatAxisValue(
  value: number,
  valueType: CrossPlotAxisValueType | undefined,
  decimals: number
): string {
  if (isDateAxis(valueType)) return parseMomentUtc(value).format('MM/DD/YYYY');
  return formatNumber(value, 0, decimals);
}

function applyAxisLabelFormat(
  provider: NumericLabelProvider | null,
  valueType: CrossPlotAxisValueType | undefined
) {
  if (!provider) return;
  provider.formatLabel = isDateAxis(valueType)
    ? formatDateTick
    : (value: number) => formatNumber(value, 0, 0);
}

// ---------------------------------------------------------------------------
// Point metadata
// ---------------------------------------------------------------------------
interface CrossPlotPointMeta extends IPointMetadata {
  uwi: string;
  wellName: string | null;
  wellQualifier: string | null;
  status: string;
  xValue: number;
  yValue: number;
}

// ---------------------------------------------------------------------------
// Highlight series IDs
// ---------------------------------------------------------------------------
const HIGHLIGHT_HALO_ID = 'highlight-halo';
const HIGHLIGHT_BORDER_ID = 'highlight-border';
const HIGHLIGHT_INNER_ID = 'highlight-inner';
const CROSS_HIGHLIGHT_HALO_ID = 'cross-highlight-halo';
const CROSS_HIGHLIGHT_BORDER_ID = 'cross-highlight-border';
const CROSS_HIGHLIGHT_INNER_ID = 'cross-highlight-inner';
const HIGHLIGHT_IDS = new Set([
  HIGHLIGHT_HALO_ID,
  HIGHLIGHT_BORDER_ID,
  HIGHLIGHT_INNER_ID,
  CROSS_HIGHLIGHT_HALO_ID,
  CROSS_HIGHLIGHT_BORDER_ID,
  CROSS_HIGHLIGHT_INNER_ID,
]);

// ---------------------------------------------------------------------------
// Tooltip modifier — mirrors ProbitTooltipModifier pattern
// ---------------------------------------------------------------------------

class CrossPlotTooltipModifier extends ChartModifierBase2D {
  readonly type = 'CrossPlotTooltipModifier';
  private tooltipEl: HTMLDivElement | null = null;
  private containerEl: HTMLDivElement | null = null;
  private highlightDs: XyDataSeries | null = null;
  private highlightHalo: EllipsePointMarker | null = null;
  private highlightInner: EllipsePointMarker | null = null;
  private pointColor = DEFAULT_POINT_COLOR;
  private onPointHover: ((uwi: string | null) => void) | null = null;
  private lastHoveredUwi: string | null = null;
  private xAxisLabel: string = 'X';
  private yAxisLabel: string = 'Y';
  private xAxisValueType: CrossPlotAxisValueType = 'number';
  private yAxisValueType: CrossPlotAxisValueType = 'number';

  setConfig(cfg: {
    tooltipEl: HTMLDivElement | null;
    containerEl: HTMLDivElement | null;
    highlightDs: XyDataSeries | null;
    highlightHalo: EllipsePointMarker | null;
    highlightInner: EllipsePointMarker | null;
    pointColor: string;
    onPointHover: ((uwi: string | null) => void) | null;
  }) {
    this.tooltipEl = cfg.tooltipEl;
    this.containerEl = cfg.containerEl;
    this.highlightDs = cfg.highlightDs;
    this.highlightHalo = cfg.highlightHalo;
    this.highlightInner = cfg.highlightInner;
    this.pointColor = cfg.pointColor;
    this.onPointHover = cfg.onPointHover;
  }

  setAxisLabels(
    xLabel: string,
    yLabel: string,
    xValueType: CrossPlotAxisValueType = 'number',
    yValueType: CrossPlotAxisValueType = 'number'
  ) {
    this.xAxisLabel = xLabel;
    this.yAxisLabel = yLabel;
    this.xAxisValueType = xValueType;
    this.yAxisValueType = yValueType;
  }

  private updateHighlight(x?: number, y?: number) {
    if (!this.highlightDs) return;
    this.highlightDs.clear();
    if (x !== undefined && y !== undefined) {
      if (this.highlightHalo) {
        this.highlightHalo.fill = `${this.pointColor}50`;
      }
      if (this.highlightInner) {
        this.highlightInner.fill = this.pointColor;
        this.highlightInner.stroke = this.pointColor;
      }
      this.highlightDs.append(x, y);
    }
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    if (!this.tooltipEl || !this.parentSurface) return;

    let closest: {
      meta: CrossPlotPointMeta;
      dist: number;
      x: number;
      y: number;
    } | null = null;

    for (const series of this.parentSurface.renderableSeries.asArray()) {
      if (!series.isVisible || HIGHLIGHT_IDS.has(series.id)) continue;
      const ht = series.hitTestProvider.hitTest(
        args.mousePoint.x,
        args.mousePoint.y,
        HIT_TEST_RADIUS
      );
      if (ht.isHit && ht.dataSeriesIndex >= 0) {
        const ds = series.dataSeries as XyDataSeries;
        const meta = ds.getMetadataAt(ht.dataSeriesIndex) as CrossPlotPointMeta;
        if (!meta) continue;
        const dist = Math.hypot(
          args.mousePoint.x - ht.hitTestPointValues.x,
          args.mousePoint.y - ht.hitTestPointValues.y
        );
        if (!closest || dist < closest.dist) {
          closest = {
            meta,
            dist,
            x: ds.getNativeXValues().get(ht.dataSeriesIndex),
            y: ds.getNativeYValues().get(ht.dataSeriesIndex),
          };
        }
      }
    }

    if (!closest) {
      this.tooltipEl.style.display = 'none';
      this.updateHighlight();
      if (this.lastHoveredUwi !== null) {
        this.lastHoveredUwi = null;
        this.onPointHover?.(null);
      }
      return;
    }

    const { meta, x, y } = closest;
    this.updateHighlight(x, y);

    if (meta.uwi !== this.lastHoveredUwi) {
      this.lastHoveredUwi = meta.uwi;
      this.onPointHover?.(meta.uwi);
    }

    const wellName = meta.wellName || '--';
    const qualifier = meta.wellQualifier || '--';
    const uwi = meta.uwi || '--';
    const xShortName = extractShortName(this.xAxisLabel);
    const yShortName = extractShortName(this.yAxisLabel);
    const xUnit = extractUnit(this.xAxisLabel);
    const yUnit = extractUnit(this.yAxisLabel);
    const xVal = formatAxisValue(meta.xValue, this.xAxisValueType, 2);
    const yVal = formatAxisValue(meta.yValue, this.yAxisValueType, 2);

    this.tooltipEl.innerHTML = `
      <div class="${styles.tooltipRow}">Well Name: ${wellName}</div>
      <div class="${styles.tooltipRow}">UWI: ${uwi}</div>
      <div class="${styles.tooltipRow}">Well Qualifier: ${qualifier}</div>
      <div class="${styles.tooltipRow}">${xShortName}: ${xVal}${
        xUnit ? ` ${xUnit}` : ''
      }</div>
      <div class="${styles.tooltipRow}">${yShortName}: ${yVal}${
        yUnit ? ` ${yUnit}` : ''
      }</div>
    `;
    this.tooltipEl.style.display = 'block';

    if (this.containerEl) {
      const rect = this.containerEl.getBoundingClientRect();
      const e = args.nativeEvent as MouseEvent;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const cW = rect.width;
      const cH = rect.height;
      const tW = this.tooltipEl.offsetWidth;
      const tH = this.tooltipEl.offsetHeight;

      let left = mx + 12;
      let top = my - tH - 8;

      if (top < 0) top = my + 12;
      if (left + tW > cW) left = mx - tW - 12;

      this.tooltipEl.style.left = `${Math.max(0, left)}px`;
      this.tooltipEl.style.top = `${Math.max(0, Math.min(top, cH - tH))}px`;
    }
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    if (this.tooltipEl) this.tooltipEl.style.display = 'none';
    this.updateHighlight();
    if (this.lastHoveredUwi !== null) {
      this.lastHoveredUwi = null;
      this.onPointHover?.(null);
    }
  }
}

// ---------------------------------------------------------------------------
// CrossPlot Component
// ---------------------------------------------------------------------------

export interface CrossPlotProps {
  data: CrossPlotData | null;
  isLoading?: boolean;
  error?: string | null;
  uwiColorMap?: Record<string, string>;
  highlightedUwi?: string | null;
  onPointHover?: (uwi: string | null) => void;
  /** Header element rendered above the chart (e.g. title row). */
  header?: ReactNode;
  /** Footer element rendered below the chart (e.g. controls / dropdowns). */
  footer?: ReactNode;
  /**
   * When provided, renders an icon-only image-export trigger in the chart
   * header (top-right) wired to this chart's SciChart surface. Omit to render
   * no trigger (default), leaving other consumers unaffected.
   */
  exportConfig?: { label: string; filenamePrefix: string };
  /**
   * Optional unique id used as the SciChart host div's `id` attribute.
   * REQUIRED to be unique when rendering multiple `CrossPlot`s on the same
   * page — SciChart derives its internal canvas id from this and silently
   * deletes earlier surfaces that share the same id (see `createMaster.js` →
   * `createChildSurface`). When omitted, a random id is generated per
   * instance so the component is still safe to use in isolation.
   */
  id?: string;
}

export const CrossPlot: FC<CrossPlotProps> = ({
  data,
  isLoading = false,
  error = null,
  uwiColorMap,
  highlightedUwi,
  onPointHover,
  header,
  footer,
  exportConfig,
  id,
}) => {
  const chartCanvasRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  // SciChart derives its internal canvas ids from `chartRef.current.id`
  // (see `createMaster.js` — `canvas2dId = divElementId + "_2D"`). When
  // multiple charts share the same host div id (or all have empty ids),
  // `createChildSurface` treats them as the same destination and deletes
  // previously-created surfaces, breaking everything except the last chart
  // (tooltips, hit-testing, hover highlights). Fall back to a random id so
  // the component stays safe to use even when callers don't supply one.
  const fallbackChartDivId = useRef(
    `cross-plot-${Math.random().toString(36).slice(2, 11)}`
  ).current;
  const chartDivId = id ?? fallbackChartDivId;
  const sciChartSurfaceRef = useRef<SciChartSurface | null>(null);
  // Scale axis-label/title fonts and series weight to the export resolution.
  const exportElementHooks = useMemo(
    () => createSciChartElementHooks(() => sciChartSurfaceRef.current),
    []
  );
  const tooltipModifierRef = useRef<CrossPlotTooltipModifier | null>(null);
  // Held onto so `renderData` can swap in the date formatter when the user
  // picks a date axis — the axes themselves are built once, on init.
  const xLabelProviderRef = useRef<NumericLabelProvider | null>(null);
  const yLabelProviderRef = useRef<NumericLabelProvider | null>(null);

  // Cross-chart highlight refs (driven by highlightedUwi prop)
  const crossHighlightDsRef = useRef<XyDataSeries | null>(null);
  const crossHighlightInnerRef = useRef<EllipsePointMarker | null>(null);
  const pointsByUwiRef = useRef<Map<string, { x: number; y: number }>>(
    new Map()
  );

  const onPointHoverRef = useRef(onPointHover);
  onPointHoverRef.current = onPointHover;

  // Track whether the surface is ready so the data useEffect can wait for it.
  const surfaceReadyRef = useRef(false);
  // Store pending data render request to replay after surface init completes.
  const pendingDataRenderRef = useRef(false);
  // Track the last (xAxis, yAxis) combo we rendered. We use this to decide
  // whether `renderData` should auto-fit (`zoomExtents`) — we want to refit
  // when the user changes axes via the modal, but preserve their wheel-zoom
  // on filter-only data refreshes that keep the same axes.
  const lastAxesKeyRef = useRef<string | null>(null);

  // Initialize chart
  useEffect(() => {
    ensureWasmConfigured();

    const initChart = async () => {
      if (!chartRef.current || sciChartSurfaceRef.current) return;

      const theme = new SciChartJSLightTheme();
      theme.sciChartBackground = '#FFFFFF';
      theme.majorGridLineBrush = '#e0e0e0';
      theme.minorGridLineBrush = '#eeeeee';
      theme.axisBandsFill = 'transparent';

      const { sciChartSurface, wasmContext } = await SciChartSurface.create(
        chartRef.current,
        { theme }
      );

      // X-axis: integer labels (no decimals)
      const xLabelProvider = new NumericLabelProvider();
      xLabelProvider.formatLabel = (value: number) => formatNumber(value, 0, 0);
      xLabelProviderRef.current = xLabelProvider;

      // NOTE: `EAutoRange.Always` continuously re-fits the axis to the data,
      // which silently undoes any user-initiated zoom — wheel-zoom appears to
      // do nothing. Use `Never` and call `surface.zoomExtents()` ourselves
      // from `renderData` whenever the axes change (see below). This mirrors
      // the pattern used in `probit-chart.tsx`.
      const xAxisObj = new NumericAxis(wasmContext, {
        axisTitle: '',
        axisTitleStyle: AXIS_TITLE_STYLE,
        labelStyle: AXIS_LABEL_STYLE,
        autoRange: EAutoRange.Never,
        growBy: new NumberRange(0.05, 0.05),
        drawMajorGridLines: true,
        drawMinorGridLines: false,
        labelProvider: xLabelProvider,
      });

      // Y-axis: integer labels (no decimals), aligned left
      const yLabelProvider = new NumericLabelProvider();
      yLabelProvider.formatLabel = (value: number) => formatNumber(value, 0, 0);
      yLabelProviderRef.current = yLabelProvider;

      const yAxisObj = new NumericAxis(wasmContext, {
        axisTitle: '',
        axisTitleStyle: AXIS_TITLE_STYLE,
        labelStyle: AXIS_LABEL_STYLE,
        axisAlignment: EAxisAlignment.Left,
        autoRange: EAutoRange.Never,
        growBy: new NumberRange(0.05, 0.05),
        drawMajorGridLines: true,
        drawMinorGridLines: false,
        labelProvider: yLabelProvider,
      });

      sciChartSurface.xAxes.add(xAxisObj);
      sciChartSurface.yAxes.add(yAxisObj);

      sciChartSurface.chartModifiers.add(new MouseWheelZoomModifier());
      sciChartSurface.chartModifiers.add(new ZoomPanModifier());
      sciChartSurface.chartModifiers.add(new XAxisDragModifier());
      sciChartSurface.chartModifiers.add(new YAxisDragModifier());
      sciChartSurface.chartModifiers.add(
        new CursorModifier({
          showTooltip: false,
          showAxisLabels: false,
          crosshairStroke: '#a8a8a8',
          crosshairStrokeDashArray: [4, 4],
        })
      );

      const tooltipMod = new CrossPlotTooltipModifier();
      sciChartSurface.chartModifiers.add(tooltipMod);
      tooltipModifierRef.current = tooltipMod;

      sciChartSurfaceRef.current = sciChartSurface;
      surfaceReadyRef.current = true;

      // If data arrived before the surface was ready, trigger a re-render.
      if (pendingDataRenderRef.current) {
        pendingDataRenderRef.current = false;
        renderData(sciChartSurface);
      }
    };

    void initChart();

    return () => {
      surfaceReadyRef.current = false;
      sciChartSurfaceRef.current?.delete();
      sciChartSurfaceRef.current = null;
      tooltipModifierRef.current = null;
    };
  }, []);

  // Render data onto the surface. Extracted so it can be called both from the
  // data useEffect and from the init useEffect when the surface becomes ready
  // after data has already arrived.
  const dataRef = useRef(data);
  const uwiColorMapRef = useRef(uwiColorMap);
  dataRef.current = data;
  uwiColorMapRef.current = uwiColorMap;

  const renderData = (surface: SciChartSurface) => {
    const currentData = dataRef.current;
    const currentColorMap = uwiColorMapRef.current;
    if (!currentData) return;

    // Clear existing series and annotations
    surface.renderableSeries.clear();
    surface.annotations.clear();

    const wasmContext = surface.webAssemblyContext2D;

    // Build UWI → coordinate lookup for cross-chart highlight
    const uwiMap = new Map<string, { x: number; y: number }>();

    // Create data series
    const xValues: number[] = [];
    const yValues: number[] = [];
    const metadata: CrossPlotPointMeta[] = [];

    // Group points by (status, color). Each unique combination becomes its own
    // renderable series so we can give it a distinct marker shape (per status)
    // while preserving the per-point color-by-attribute behaviour.
    const pointGroups = new Map<
      string,
      {
        statusKey: string;
        color: string;
        x: number[];
        y: number[];
        meta: CrossPlotPointMeta[];
      }
    >();

    for (const point of currentData.dataPoints) {
      const ptColor = currentColorMap?.[point.uwi] || DEFAULT_POINT_COLOR;
      const statusKey = normalizeStatus(point.status);
      const meta: CrossPlotPointMeta = {
        isSelected: false,
        uwi: point.uwi,
        wellName: point.wellName,
        wellQualifier: point.wellQualifier,
        status: point.status,
        xValue: point.x,
        yValue: point.y,
      };

      xValues.push(point.x);
      yValues.push(point.y);
      metadata.push(meta);

      if (point.uwi) uwiMap.set(point.uwi, { x: point.x, y: point.y });

      const groupKey = `${statusKey}::${ptColor}`;
      let group = pointGroups.get(groupKey);
      if (!group) {
        group = { statusKey, color: ptColor, x: [], y: [], meta: [] };
        pointGroups.set(groupKey, group);
      }
      group.x.push(point.x);
      group.y.push(point.y);
      group.meta.push(meta);
    }

    pointsByUwiRef.current = uwiMap;

    // Render one scatter series per (status, color) bucket, picking the marker
    // shape from STATUS_MARKER. Unfilled statuses (EXPIRED_PERMIT, INACTIVE)
    // get a transparent fill so only the outline shows.
    let seriesIdx = 0;
    for (const group of pointGroups.values()) {
      const ds = new XyDataSeries(wasmContext, {
        xValues: group.x,
        yValues: group.y,
        metadata: group.meta,
        dataSeriesName:
          seriesIdx === 0 ? 'Wells' : `Wells-${group.statusKey || seriesIdx}`,
      });

      surface.renderableSeries.add(
        new XyScatterRenderableSeries(wasmContext, {
          id: `wells-${seriesIdx}`,
          dataSeries: ds,
          pointMarker: buildPointMarker(
            wasmContext,
            group.statusKey,
            group.color
          ),
          opacity: 0.85,
        })
      );
      seriesIdx++;
    }

    // 3-layer hover highlight: halo → white border → inner dot
    const highlightDs = new XyDataSeries(wasmContext);

    const highlightHaloMarker = new EllipsePointMarker(wasmContext, {
      width: 25,
      height: 25,
      fill: `${DEFAULT_POINT_COLOR}50`,
      stroke: 'transparent',
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasmContext, {
        id: HIGHLIGHT_HALO_ID,
        dataSeries: highlightDs,
        pointMarker: highlightHaloMarker,
        opacity: 1,
      })
    );

    const highlightBorderMarker = new EllipsePointMarker(wasmContext, {
      width: 14,
      height: 14,
      fill: '#ffffff',
      stroke: '#ffffff',
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasmContext, {
        id: HIGHLIGHT_BORDER_ID,
        dataSeries: highlightDs,
        pointMarker: highlightBorderMarker,
        opacity: 1,
      })
    );

    const highlightInnerMarker = new EllipsePointMarker(wasmContext, {
      width: 10,
      height: 10,
      fill: DEFAULT_POINT_COLOR,
      stroke: DEFAULT_POINT_COLOR,
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasmContext, {
        id: HIGHLIGHT_INNER_ID,
        dataSeries: highlightDs,
        pointMarker: highlightInnerMarker,
        opacity: 1,
      })
    );

    // Cross-chart highlight (3-layer, same as hover but driven by prop)
    const crossDs = new XyDataSeries(wasmContext);
    crossHighlightDsRef.current = crossDs;

    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasmContext, {
        id: CROSS_HIGHLIGHT_HALO_ID,
        dataSeries: crossDs,
        pointMarker: new EllipsePointMarker(wasmContext, {
          width: 25,
          height: 25,
          fill: `${DEFAULT_POINT_COLOR}50`,
          stroke: 'transparent',
          strokeThickness: 0,
        }),
        opacity: 1,
      })
    );
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasmContext, {
        id: CROSS_HIGHLIGHT_BORDER_ID,
        dataSeries: crossDs,
        pointMarker: new EllipsePointMarker(wasmContext, {
          width: 14,
          height: 14,
          fill: '#ffffff',
          stroke: '#ffffff',
          strokeThickness: 0,
        }),
        opacity: 1,
      })
    );
    crossHighlightInnerRef.current = new EllipsePointMarker(wasmContext, {
      width: 10,
      height: 10,
      fill: DEFAULT_POINT_COLOR,
      stroke: DEFAULT_POINT_COLOR,
      strokeThickness: 0,
    });
    surface.renderableSeries.add(
      new XyScatterRenderableSeries(wasmContext, {
        id: CROSS_HIGHLIGHT_INNER_ID,
        dataSeries: crossDs,
        pointMarker: crossHighlightInnerRef.current,
        opacity: 1,
      })
    );

    // Configure tooltip modifier with refs + highlight data series
    tooltipModifierRef.current?.setConfig({
      tooltipEl: tooltipRef.current ?? null,
      containerEl: chartCanvasRef.current ?? null,
      highlightDs,
      highlightHalo: highlightHaloMarker,
      highlightInner: highlightInnerMarker,
      pointColor: DEFAULT_POINT_COLOR,
      onPointHover: onPointHoverRef.current ?? null,
    });

    // Extract units from axis labels for line captions
    const xUnit = extractUnit(currentData.xAxisLabel);
    const yUnit = extractUnit(currentData.yAxisLabel);

    applyAxisLabelFormat(xLabelProviderRef.current, currentData.xAxisValueType);
    applyAxisLabelFormat(yLabelProviderRef.current, currentData.yAxisValueType);

    // Median lines — dashed black, no labels (kept intentionally — the median
    // values are surfaced via the tooltip / summary, not on the chart itself).
    if (currentData.xMedian !== null) {
      surface.annotations.add(
        new VerticalLineAnnotation({
          x1: currentData.xMedian,
          stroke: '#161616',
          strokeThickness: 1.5,
          strokeDashArray: [6, 4],
          annotationLayer: EAnnotationLayer.BelowChart,
        })
      );
    }

    if (currentData.yMedian !== null) {
      surface.annotations.add(
        new HorizontalLineAnnotation({
          y1: currentData.yMedian,
          stroke: '#161616',
          strokeThickness: 1.5,
          strokeDashArray: [6, 4],
          annotationLayer: EAnnotationLayer.BelowChart,
        })
      );
    }

    // Type Curve lines — solid golden, with a "TC: <value> <unit>" label on
    // each line. The labels mirror the probit-chart pattern: an explicit
    // width/height on the inline <svg> sets a real viewport so the text isn't
    // clipped by the default `overflow: hidden` on nested SVGs (which is why
    // a label drawn at a negative y would silently disappear).
    const buildTcLabelSvg = (text: string) =>
      `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="16">` +
      `<text x="4" y="12" text-anchor="start" fill="#b28600" font-size="11" font-weight="500">${text}</text>` +
      `</svg>`;

    if (currentData.xTypeCurve !== null) {
      surface.annotations.add(
        new VerticalLineAnnotation({
          x1: currentData.xTypeCurve,
          stroke: '#b28600',
          strokeThickness: 2,
          annotationLayer: EAnnotationLayer.BelowChart,
        })
      );
      const xTcText = `TC: ${formatAxisValue(
        currentData.xTypeCurve,
        currentData.xAxisValueType,
        0
      )}${xUnit ? ` ${xUnit}` : ''}`;
      surface.annotations.add(
        new CustomAnnotation({
          x1: currentData.xTypeCurve,
          y1: 0.02,
          verticalAnchorPoint: EVerticalAnchorPoint.Top,
          horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
          xCoordinateMode: ECoordinateMode.DataValue,
          yCoordinateMode: ECoordinateMode.Relative,
          svgString: buildTcLabelSvg(xTcText),
        })
      );
    }

    if (currentData.yTypeCurve !== null) {
      surface.annotations.add(
        new HorizontalLineAnnotation({
          y1: currentData.yTypeCurve,
          stroke: '#b28600',
          strokeThickness: 2,
          annotationLayer: EAnnotationLayer.BelowChart,
        })
      );
      const yTcText = `TC: ${formatAxisValue(
        currentData.yTypeCurve,
        currentData.yAxisValueType,
        0
      )}${yUnit ? ` ${yUnit}` : ''}`;
      surface.annotations.add(
        new CustomAnnotation({
          x1: 0,
          y1: currentData.yTypeCurve,
          verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
          horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
          xCoordinateMode: ECoordinateMode.Relative,
          yCoordinateMode: ECoordinateMode.DataValue,
          // Nudge the label a few pixels above the line so its glyphs don't
          // overlap the 2px-thick golden stroke.
          yCoordShift: -4,
          svgString: buildTcLabelSvg(yTcText),
        })
      );
    }

    // Update axis labels
    surface.xAxes.get(0).axisTitle = currentData.xAxisLabel;
    surface.yAxes.get(0).axisTitle = currentData.yAxisLabel;

    tooltipModifierRef.current?.setAxisLabels(
      currentData.xAxisLabel,
      currentData.yAxisLabel,
      currentData.xAxisValueType,
      currentData.yAxisValueType
    );

    // Fit the chart to the data on first render and whenever the axes change
    // (different metric ⇒ different units / scale). For filter-only refreshes
    // that keep the same axes we leave the visible range alone so the user's
    // wheel-zoom is preserved.
    const axesKey = `${currentData.xAxis}|${currentData.yAxis}`;
    if (lastAxesKeyRef.current !== axesKey) {
      lastAxesKeyRef.current = axesKey;
      surface.zoomExtents();
    }
  };

  // Update data when data or color map changes
  useEffect(() => {
    const surface = sciChartSurfaceRef.current;
    if (!data) return;

    if (!surface || !surfaceReadyRef.current) {
      // Surface not ready yet — mark as pending so init callback can pick it up.
      pendingDataRenderRef.current = true;
      return;
    }

    renderData(surface);
  }, [data, uwiColorMap]);

  // Resize observer — keep the SciChart canvas div in sync with the wrapper
  // size. SciChart requires explicit pixel dimensions on its host div, so we
  // mirror probit-chart's approach: observe the canvas wrapper and push width /
  // height onto the chart div whenever the layout changes.
  const handleResize = useMemo(
    () =>
      debounce((entries: ResizeObserverEntry[]) => {
        const { width, height } = entries[entries.length - 1].contentRect;
        const divEl = chartRef.current;
        if (divEl) {
          divEl.style.width = `${width}px`;
          divEl.style.height = `${height}px`;
        }
      }, 100),
    []
  );

  useEffect(() => {
    if (!chartCanvasRef.current) return;
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(chartCanvasRef.current);
    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
    };
  }, [handleResize]);

  // Cross-chart highlight — driven by `highlightedUwi` prop
  useEffect(() => {
    const ds = crossHighlightDsRef.current;
    if (!ds) return;
    ds.clear();

    if (!highlightedUwi) return;
    const pt = pointsByUwiRef.current.get(highlightedUwi);
    if (!pt) return;

    // Resolve the point's color from the color map
    const ptColor = uwiColorMap?.[highlightedUwi] ?? DEFAULT_POINT_COLOR;
    const inner = crossHighlightInnerRef.current;
    if (inner) {
      inner.fill = ptColor;
      inner.stroke = ptColor;
    }

    ds.append(pt.x, pt.y);
  }, [highlightedUwi, uwiColorMap]);

  return (
    <div className={styles.chartWrapper}>
      {header}
      {exportConfig && (
        <SciChartExportImageButton
          iconOnly
          surfaceRef={sciChartSurfaceRef}
          containerRef={chartCanvasRef}
          chartLabel={exportConfig.label}
          filenamePrefix={exportConfig.filenamePrefix}
          elementHooks={exportElementHooks}
        />
      )}
      <div ref={chartCanvasRef} className={styles.chartCanvasWrapper}>
        <div ref={chartRef} id={chartDivId} className={styles.chartCanvas} />
        <div ref={tooltipRef} className={styles.crossPlotTooltip} />
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>Loading...</div>
          </div>
        )}
        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>
      {footer}
    </div>
  );
};
